import { CloseOutlined, PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Spin, Tooltip } from "antd";
import { WaveDotsLoader } from "../wave-dots-loader";
import React from "react";
import { InputMention } from "@src/components/input-menton";
import { Mention } from "react-mentions";
import { getMemberColor } from "@src/util/intelligence-search/get-member-info";
import { getDataTypeIcon } from "@src/pages/intelligence-search/constant/data-type-icons";
import { TModalCustomMetricState } from "./search-template";
import { InputMentionValue } from "@src/types/intelligence-search";
import { css } from "@emotion/react";

export type TSearchCustomMetricTemplateProps = {
    openCustomMetricModalState?: TModalCustomMetricState;
    handleCloseCustomMetricModal?: () => void;

    additionalValidator?: {
        formula?: (value: string, initValue?: string) => Promise<void>;
        alias?: (value: string, initValue?: string) => Promise<void>;
    };
    handleConfirm: (
        formula: string,
        alias: string,
        mentionMeta: InputMentionValue,
        onSuccess?: () => void,
        onError?: (error: { field: "formula" | "alias"; error: string[] }[]) => void,
        editProps?: {
            id: string;
        },
    ) => Promise<void>;
    loading?: boolean;
    options: { id: string; display: string; meta: Record<string, any> }[];
};
export const SearchCustomMetric: React.FC<TSearchCustomMetricTemplateProps> = ({
    handleConfirm,
    loading,
    options,
    openCustomMetricModalState,
    handleCloseCustomMetricModal,
    additionalValidator,
}) => {
    const isEdit = !!openCustomMetricModalState?.formula;
    const open = !!openCustomMetricModalState?.open;
    const [form] = Form.useForm();

    const [isPreventAutoGenerateAlias, setIsPreventAutoGenerateAlias] = React.useState(false);
    const formulaCustomMetricWatch: InputMentionValue = Form.useWatch("formula", form);
    const aliasCustomMetricWatch = Form.useWatch("alias", form);
    const [aliasError, setAliasError] = React.useState<string[]>([]);
    const [formulaError, setFormulaError] = React.useState<string[]>([]);

    const setFieldFormulaError = (error: string[]) => {
        form.setFields([
            {
                name: "formula",
                errors: error,
            },
        ]);
        setFormulaError(error);
    };
    const setFieldAliasError = (error: string[]) => {
        form.setFields([
            {
                name: "alias",
                errors: error,
            },
        ]);
        setAliasError(error);
    };

    const handleClose = () => {
        setFormulaError([]);
        form.resetFields(["formula", "alias"]);
        handleCloseCustomMetricModal?.();
    };
    const validateForm = async () => {
        // Validate form fields
        return await form.validateFields(["alias", "formula"]);
    };
    const handleOk = async () => {
        try {
            await validateForm();
            await handleConfirm(
                extractIdsFromMarkup(formulaCustomMetricWatch?.newValue),
                aliasCustomMetricWatch,
                formulaCustomMetricWatch,
                () => {
                    handleClose();
                },
                (errorArg) => {
                    errorArg.forEach(({ field, error }) => {
                        if (field === "formula") {
                            setFieldFormulaError(error);
                        }
                        if (field === "alias") {
                            setFieldAliasError(error);
                        }
                    });
                },
                isEdit
                    ? {
                          id: openCustomMetricModalState?.meta?.id ?? "",
                      }
                    : undefined,
            );
        } catch (error) {
            console.log("Validation failed:", error);
        }
    };

    const isAliasGenerated = generateValidAlias(formulaCustomMetricWatch?.newPlainTextValue) === aliasCustomMetricWatch;

    const renderSuggestion = (
        suggestion: any,
        search: string,
        highlightedDisplay: React.ReactNode,
        index: number,
        focused: boolean,
    ) => {
        return (
            <div
                className={`flex items-center p-2 w-[400px] ${focused ? "bg-blue-50" : ""}`}
                style={{
                    color: getMemberColor(suggestion.meta.asset_type).textColor,
                }}
            >
                <div className="mr-2">
                    {getDataTypeIcon({
                        type: suggestion.meta.data_type,
                        size: 16,
                    })}
                </div>
                <div className="flex flex-col">
                    <div className="text-sm font-medium">{highlightedDisplay}</div>
                    <div className="text-xs text-gray-500">{suggestion.meta.name}</div>
                </div>
            </div>
        );
    };

    React.useEffect(() => {
        if (!open) return;
        const timeout = setTimeout(() => {
            const mentionMeta = openCustomMetricModalState?.meta;
            if (mentionMeta) {
                const initFormula: InputMentionValue = {
                    newValue: mentionMeta?.newValue ?? "",
                    newPlainTextValue: mentionMeta?.newPlainTextValue ?? "",
                    mentions: mentionMeta?.mentions ?? [],
                };
                form.setFieldsValue({
                    formula: initFormula,
                    alias: openCustomMetricModalState?.alias,
                });
            }
        }, 100);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, openCustomMetricModalState]);
    return (
        <Modal
            open={open}
            centered
            afterClose={() => handleClose()}
            closeIcon={<Button type="text" icon={<CloseOutlined />} onClick={handleClose}></Button>}
            afterOpenChange={() => {
                if (isEdit) {
                    setIsPreventAutoGenerateAlias(true);
                } else {
                    setIsPreventAutoGenerateAlias(false);
                }
            }}
            width={800}
            footer={() => {
                return (
                    <div className="flex justify-end gap-2">
                        <Button type="text" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="primary" onClick={() => handleOk()} loading={loading}>
                            Validate & Create
                        </Button>
                    </div>
                );
            }}
        >
            <Form form={form} layout="vertical">
                <Spin
                    spinning={loading}
                    tip={
                        <div className="flex items-center justify-center gap-2 w-full">
                            <WaveDotsLoader color="blue" /> Validating this custom metric
                        </div>
                    }
                >
                    <div
                        className="flex flex-col"
                        css={css`
                            .ant-form-item {
                                margin-bottom: 8px !important;
                            }
                        `}
                    >
                        <Form.Item
                            layout="vertical"
                            label="Formula"
                            name={"formula"}
                            required
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (!value?.newValue) {
                                            setFieldFormulaError(["Formula is required"]);
                                            return Promise.reject(new Error("Formula is required"));
                                        }
                                        if (additionalValidator?.formula) {
                                            return additionalValidator.formula(
                                                value,
                                                openCustomMetricModalState?.formula,
                                            );
                                        }
                                        setFieldFormulaError([]);
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputMention
                                onAfterChange={(value) => {
                                    if (isPreventAutoGenerateAlias) return;
                                    const validAlias = generateValidAlias(value.newPlainTextValue);
                                    form.setFieldValue("alias", validAlias);
                                }}
                                status={formulaError?.length > 0 ? "error" : ""}
                                placeholder="Type @ to select and insert input metrics into the formula."
                                singleLine={false}
                            >
                                <Mention
                                    trigger="@"
                                    data={options}
                                    renderSuggestion={renderSuggestion}
                                    markup="@[__display__](__id__)"
                                    displayTransform={(id) => `@${id}`}
                                    appendSpaceOnAdd={true}
                                    style={{
                                        backgroundColor: getMemberColor("metric").backgroundColorSecondary,
                                    }}
                                />
                            </InputMention>
                        </Form.Item>
                        <ul className="m-0 pl-4 text-gray-500 text-[12px] mb-2">
                            <li>Input formula to create custom metric</li>
                            <li>
                                Formula supports basic operations like addition (+), subtraction (−), multiplication
                                (*), and division (/)
                            </li>
                        </ul>
                        <div className="w-0 h-2"></div>
                        <div className="flex gap-2 items-center">
                            <Form.Item
                                className="flex-1"
                                layout="vertical"
                                label="Alias name"
                                name={"alias"}
                                required
                                status={aliasError?.length > 0 ? "error" : ""}
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            const ctxPattern = "^[A-Za-z_][A-Za-z0-9_]{0,300}$";
                                            if (!value.match(ctxPattern)) {
                                                return Promise.reject(new Error("Invalid alias name"));
                                            }

                                            if (additionalValidator?.alias) {
                                                return additionalValidator.alias(
                                                    value,
                                                    openCustomMetricModalState?.alias,
                                                );
                                            }

                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Input alias name for custom metric"
                                    onChange={() => {
                                        setIsPreventAutoGenerateAlias(true);
                                    }}
                                />
                            </Form.Item>
                            <Tooltip title={isAliasGenerated ? "" : "Auto generate alias"}>
                                <Button
                                    className={`text-[16px] mt-2  ${
                                        isAliasGenerated ? "text-gray-300" : "text-orange-500"
                                    }`}
                                    icon={<SyncOutlined />}
                                    type="text"
                                    disabled={isAliasGenerated}
                                    onClick={() => {
                                        const validAlias = generateValidAlias(
                                            formulaCustomMetricWatch?.newPlainTextValue,
                                        );
                                        form.setFieldValue("alias", validAlias);
                                        form.validateFields(["alias"]);
                                    }}
                                />
                            </Tooltip>
                        </div>
                        <ul className="m-0 pl-4 text-gray-500 text-[12px] mb-2">
                            <li>
                                Use letters, numbers, or underscores. Special characters and spaces aren’t supported.
                            </li>
                        </ul>
                    </div>
                </Spin>
            </Form>
        </Modal>
    );
};

// Helper function to generate valid alias from formula text
const generateValidAlias = (formulaText: string): string => {
    if (!formulaText) return "";
    const regex = /[^a-zA-Z0-9_]/g;
    const cleanText = formulaText.replace(regex, "_");
    const validAlias = cleanText.replace(/_{2,}/g, "_").replace(/^_+|_+$/g, "");
    return validAlias || "custom_metric";
};

const extractIdsFromMarkup = (markup: string): string => {
    if (!markup) return "";

    // Regular expression to match @[display](id) pattern
    const regex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let result = markup;
    let match;

    // Replace all occurrences of @[display](id) with just the id
    while ((match = regex.exec(markup)) !== null) {
        const fullMatch = match[0]; // @[display](id)
        const id = match[2]; // id part
        result = result.replace(fullMatch, id);
    }

    return result.trim();
};
